import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, X } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../App";
import { requestPushPermissionAndSubscribe, pushPermissionStatus, isPushSupported } from "../lib/push";
import { toast } from "sonner";

const PushPrompt = () => {
  const { t } = useTranslation();
  const { token, isAuthenticated } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      if (!(await isPushSupported())) return;
      const status = pushPermissionStatus();
      const dismissed = localStorage.getItem("gentleflow_push_dismissed_at");
      const dismissedRecent = dismissed && (Date.now() - Number(dismissed)) < 1000 * 60 * 60 * 24 * 3;
      if (status === "default" && !dismissedRecent) {
        setTimeout(() => setShow(true), 4000);
      }
    })();
  }, [isAuthenticated]);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await requestPushPermissionAndSubscribe(token);
      if (r.ok) {
        toast.success("Notifications enabled");
      } else if (r.reason === "denied") {
        toast.info("You blocked notifications. Enable them from your browser settings.");
      } else {
        toast.error("Could not enable notifications on this browser.");
      }
    } finally {
      setBusy(false);
      setShow(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem("gentleflow_push_dismissed_at", String(Date.now()));
    setShow(false);
  };

  if (!show) return null;
  return (
    <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-[60] max-w-xs bg-white rounded-2xl shadow-2xl border border-[#F2E4EB] p-5 animate-[gf-fadeInUp_0.5s_ease-out]" data-testid="push-prompt">
      <button onClick={dismiss} className="absolute top-2 right-2 text-[#8A7A85]" data-testid="push-close">
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <div className="w-10 h-10 rounded-full bg-[#C084A0]/15 flex items-center justify-center mb-3">
        <Bell className="w-5 h-5 text-[#C084A0]" strokeWidth={1.5} />
      </div>
      <h4 className="font-heading text-lg text-[#2E1D28]">{t("push.title")}</h4>
      <p className="text-sm text-[#5B4956] mt-1 mb-4">{t("push.body")}</p>
      <div className="flex flex-col gap-2">
        <Button onClick={enable} disabled={busy} className="rounded-full bg-[#C084A0] hover:bg-[#A96B87] text-white" data-testid="push-enable">
          {busy ? "…" : t("push.enable")}
        </Button>
        <button onClick={dismiss} className="text-xs text-[#8A7A85] hover:text-[#2E1D28]" data-testid="push-later">
          {t("push.later")}
        </button>
      </div>
    </div>
  );
};

export default PushPrompt;
